import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  MessageSquare,
  Send,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  User
} from 'lucide-react';

const SupportTickets = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyMessage, setReplyMessage] = useState('');
  
  // New ticket form
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    category: 'technical',
    priority: 'medium'
  });

  useEffect(() => {
    if (user) {
      loadMyTickets();
    }
  }, [user]);

  const loadMyTickets = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/my-tickets`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
      }
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTicketReplies = async (ticketId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/support-tickets/${ticketId}/replies`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setReplies(data.replies || []);
      }
    } catch (error) {
      console.error('Error loading replies:', error);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    
    if (!newTicket.subject.trim() || !newTicket.description.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please fill in all required fields"
      });
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/support-tickets`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({
            ...newTicket,
            user_name: user.full_name || user.email,
            user_email: user.email
          })
        }
      );

      if (response.ok) {
        toast({
          title: "Success",
          description: "Support ticket created successfully. You'll receive an email confirmation."
        });
        setNewTicket({
          subject: '',
          description: '',
          category: 'technical',
          priority: 'medium'
        });
        loadMyTickets();
      } else {
        throw new Error('Failed to create ticket');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create support ticket"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendReply = async (ticketId) => {
    if (!replyMessage.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter a message"
      });
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/support-tickets/${ticketId}/replies`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({
            message: replyMessage
          })
        }
      );

      if (response.ok) {
        toast({
          title: "Success",
          description: "Reply sent successfully"
        });
        setReplyMessage('');
        loadTicketReplies(ticketId);
        loadMyTickets();
      } else {
        throw new Error('Failed to send reply');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to send reply"
      });
    }
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    loadTicketReplies(ticket.id);
  };

  const getStatusBadge = (status) => {
    const variants = {
      open: { color: 'bg-blue-500', text: 'Open' },
      in_progress: { color: 'bg-yellow-500', text: 'In Progress' },
      resolved: { color: 'bg-green-500', text: 'Resolved' },
      closed: { color: 'bg-gray-500', text: 'Closed' }
    };
    const variant = variants[status] || variants.open;
    return <Badge className={`${variant.color} text-white`}>{variant.text}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      low: 'bg-gray-400',
      medium: 'bg-blue-400',
      high: 'bg-orange-400',
      urgent: 'bg-red-500'
    };
    return <Badge className={`${variants[priority] || variants.medium} text-white`}>{priority}</Badge>;
  };

  const openTickets = tickets.filter(t => t.status === 'open' || t.status === 'in_progress');

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100">
      <Header />
      
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Button variant="ghost" onClick={() => navigate('/profile')} className="mr-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Profile
            </Button>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <MessageSquare className="w-8 h-8 mr-3 text-amber-600" />
              Support Tickets
            </h1>
          </div>
        </div>

        {/* View Ticket Detail */}
        {selectedTicket ? (
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  Ticket: {selectedTicket.subject}
                </CardTitle>
                <Button variant="outline" onClick={() => setSelectedTicket(null)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Tickets
                </Button>
              </div>
              <div className="flex items-center gap-2 mt-2">
                {getStatusBadge(selectedTicket.status)}
                {getPriorityBadge(selectedTicket.priority)}
                <Badge variant="outline">{selectedTicket.category}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Original Message */}
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <div className="flex items-center mb-2">
                    <User className="w-4 h-4 mr-2 text-gray-600" />
                    <span className="font-medium text-sm">{selectedTicket.user_name}</span>
                    <span className="text-xs text-gray-500 ml-2">
                      {new Date(selectedTicket.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-gray-700">{selectedTicket.message}</p>
                </div>

                <Separator />

                {/* Replies */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg">Conversation</h3>
                  {replies.length === 0 ? (
                    <p className="text-gray-500 text-sm">No replies yet</p>
                  ) : (
                    replies.map((reply) => (
                      <div 
                        key={reply.id} 
                        className={`p-4 rounded-lg border ${
                          reply.is_admin 
                            ? 'bg-amber-50 border-amber-200' 
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center mb-2">
                          <User className="w-4 h-4 mr-2" />
                          <span className="font-medium text-sm">
                            {reply.sender_name}
                            {reply.is_admin && (
                              <Badge className="ml-2 bg-amber-600 text-white text-xs">Admin</Badge>
                            )}
                          </span>
                          <span className="text-xs text-gray-500 ml-2">
                            {new Date(reply.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-gray-700 text-sm">{reply.message}</p>
                      </div>
                    ))
                  )}
                </div>

                {/* Reply Form */}
                {selectedTicket.status !== 'closed' && (
                  <div className="space-y-2">
                    <Label>Your Reply</Label>
                    <Textarea
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Type your response here..."
                      rows={4}
                    />
                    <Button onClick={() => handleSendReply(selectedTicket.id)}>
                      <Send className="w-4 h-4 mr-2" />
                      Send Reply
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Create New Ticket */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Create Support Ticket</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateTicket} className="space-y-4">
                  <div>
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      value={newTicket.subject}
                      onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})}
                      placeholder="Brief description of your issue"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      value={newTicket.description}
                      onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                      placeholder="Provide detailed information about your issue..."
                      rows={5}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="category">Category</Label>
                      <Select
                        value={newTicket.category}
                        onValueChange={(value) => setNewTicket({...newTicket, category: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="technical">Technical Issue</SelectItem>
                          <SelectItem value="billing">Billing</SelectItem>
                          <SelectItem value="account">Account</SelectItem>
                          <SelectItem value="feature">Feature Request</SelectItem>
                          <SelectItem value="bug">Bug Report</SelectItem>
                          <SelectItem value="content">Content</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="priority">Priority</Label>
                      <Select
                        value={newTicket.priority}
                        onValueChange={(value) => setNewTicket({...newTicket, priority: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button type="submit" disabled={submitting} className="w-full">
                    <Send className="w-4 h-4 mr-2" />
                    {submitting ? 'Submitting...' : 'Submit Ticket'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Open Tickets */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-amber-600" />
                  Your Open Support Tickets ({openTickets.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-center text-gray-500">Loading tickets...</p>
                ) : openTickets.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <p className="text-gray-600">No open tickets. You're all caught up!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {openTickets.map((ticket) => (
                      <div
                        key={ticket.id}
                        className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => handleViewTicket(ticket)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-lg">{ticket.subject}</h3>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(ticket.status)}
                            {getPriorityBadge(ticket.priority)}
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-2 line-clamp-2">{ticket.message}</p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>Created: {new Date(ticket.created_at).toLocaleDateString()}</span>
                          <span>Category: {ticket.category}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default SupportTickets;
